import * as React from "react";
import { Box, Typography, useTheme, MenuItem, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import InventoryIcon  from "@mui/icons-material/Inventory";
import CategoryIcon   from "@mui/icons-material/Category";
import StarIcon       from "@mui/icons-material/Star";
import FilterListIcon from "@mui/icons-material/FilterList";
import BarChartIcon   from "@mui/icons-material/BarChart";

import BarChart      from "components/Graphs/BarChart";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { useItemDashboard } from "api/hooks/dashboards";
import { useYearsList }     from "api/hooks/inventory";
import { currencyFormatter } from "utilities/currencyFormatter";

const PERIODS = ["DAY", "WEEK", "MONTH", "QUARTER", "YEAR"];

const inputSx = (cardBg, border) => ({
  "& .MuiInputBase-root": { fontSize: "13px", height: "36px", backgroundColor: cardBg, borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
  "& .MuiInputLabel-root": { fontSize: "12px" },
});

const SectionHeading = ({ icon, title, subtitle, accent, textPri, textSec }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "1rem" }}>
    <Box sx={{
      width: "32px", height: "32px", borderRadius: "8px",
      backgroundColor: "rgba(29,158,117,0.12)",
      display: "flex", alignItems: "center", justifyContent: "center",
      color: accent, flexShrink: 0,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 500, color: textPri, lineHeight: 1.2 }}>{title}</Typography>
      {subtitle && <Typography sx={{ fontSize: "11px", color: textSec, mt: "1px" }}>{subtitle}</Typography>}
    </Box>
  </Box>
);

const StatCard = ({ label, value, accent, textPri, textSec, cardBg, border, alert = false }) => (
  <Box sx={{
    flex: "1 1 150px",
    backgroundColor: alert ? (cardBg === "#ffffff" ? "#fff8f8" : "rgba(226,75,74,0.06)") : cardBg,
    border: `1px solid ${alert ? "rgba(226,75,74,0.25)" : border}`,
    borderRadius: "12px",
    p: "1rem 1.25rem",
    display: "flex", flexDirection: "column", gap: "6px",
  }}>
    <Typography sx={{ fontSize: "11px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: alert ? "#e24b4a" : textSec }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize: "20px", fontWeight: 500, color: alert ? "#e24b4a" : textPri, lineHeight: 1.2 }}>
      {value ?? "—"}
    </Typography>
    <Box sx={{ height: "2px", width: "24px", borderRadius: "2px", backgroundColor: alert ? "#e24b4a" : accent, mt: "2px" }} />
  </Box>
);

export default function ProductDashboard() {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const bodyBg  = theme.palette.background.alt;
  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const { itemDashboard, setPeriodType, setPeriod } = useItemDashboard(business_branch_id);
  const { yearList } = useYearsList();

  const [periodSelect, setPeriodSelect] = useState("DAY");
  const [quarterYear, setQuarterYear]   = useState(moment().format("YYYY"));

  const handlePeriodTypeSelect = (val) => {
    setPeriodSelect(val);
    setPeriodType(val);
  };

  const outOfStockCount    = itemDashboard?.out_of_stock_count  ?? 0;
  const expiredStockCount  = itemDashboard?.expired_stock_count ?? 0;
  const totalNumberOfItems = itemDashboard?.item_count          ?? 0;
  const itemsByRevenue     = itemDashboard?.item_revenue_sales  ?? [];
  const itemsBySales       = itemDashboard?.item_units_sales    ?? [];
  const categoryList       = itemDashboard?.top_categories      ?? [];

  const itemRevenues     = itemsByRevenue.map((i) => i.product_name);
  const itemRevenuesData = itemsByRevenue.map((i) => i.item_revenue);
  const itemSales        = itemsBySales.map((i) => i.product_name);
  const itemSalesData    = itemsBySales.map((i) => i.item_units);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Box sx={{
            width: "40px", height: "40px", borderRadius: "10px",
            backgroundColor: "rgba(29,158,117,0.12)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: accent, flexShrink: 0,
          }}>
            <InventoryIcon sx={{ fontSize: "22px" }} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "22px", fontWeight: 400, color: textPri, lineHeight: 1.2 }}>
              Item Dashboard
            </Typography>
            <Typography sx={{ fontSize: "12px", color: textSec, mt: "2px" }}>
              {moment().format("dddd, D MMMM YYYY")}
            </Typography>
          </Box>
        </Box>

        {/* Filter bar */}
        <Box sx={{
          display: "flex", alignItems: "center", flexWrap: "wrap", gap: "0.75rem",
          p: "0.75rem 1rem",
          backgroundColor: cardBg,
          border: `1px solid ${border}`,
          borderRadius: "10px",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", color: accent }}>
            <FilterListIcon sx={{ fontSize: "16px" }} />
            <Typography sx={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.07em" }}>
              Period
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "5px" }}>
            {PERIODS.map((p) => (
              <Box key={p} onClick={() => handlePeriodTypeSelect(p)} sx={{
                px: "0.65rem", py: "0.25rem", borderRadius: "20px",
                fontSize: "12px", fontWeight: 500, cursor: "pointer",
                border: `1px solid ${periodSelect === p ? accent : border}`,
                color: periodSelect === p ? accent : textSec,
                backgroundColor: periodSelect === p ? "rgba(29,158,117,0.1)" : "transparent",
                transition: "all 0.15s",
                "&:hover": { borderColor: accent, color: accent },
              }}>
                {p.charAt(0) + p.slice(1).toLowerCase()}
              </Box>
            ))}
          </Box>
          {periodSelect === "DAY" && (
            <TextField label="Date" size="small" type="date"
              defaultValue={moment().format("YYYY-MM-DD")}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "WEEK" && (
            <TextField label="Week" size="small" type="week"
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "MONTH" && (
            <TextField label="Month" size="small" type="month"
              defaultValue={moment().format("YYYY-MM")}
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "150px" }} />
          )}
          {periodSelect === "YEAR" && (
            <TextField label="Year" size="small" select
              onChange={(e) => setPeriod(e.target.value)}
              sx={{ ...inputSx(cardBg, border), width: "110px" }}>
              {yearList.map((y) => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
          )}
          {periodSelect === "QUARTER" && (
            <>
              <TextField label="Quarter" size="small" select
                onChange={(e) => setPeriod(`${quarterYear}-${e.target.value}`)}
                sx={{ ...inputSx(cardBg, border), width: "100px" }}>
                {["Q1","Q2","Q3","Q4"].map((q) => <MenuItem key={q} value={q}>{q}</MenuItem>)}
              </TextField>
              <TextField label="Year" size="small" select
                defaultValue={moment().format("YYYY")}
                onChange={(e) => setQuarterYear(e.target.value)}
                sx={{ ...inputSx(cardBg, border), width: "100px" }}>
                {yearList.map((y) => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
              </TextField>
            </>
          )}
        </Box>
      </Box>

      {/* ── Stat cards ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <StatCard label="Stock Items"   value={totalNumberOfItems}
          accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Best Seller"   value={itemsByRevenue[0]?.product_name ?? "—"}
          accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Best Category" value={categoryList[0]?.category_name ?? "—"}
          accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} />
        <StatCard label="Out of Stock"  value={outOfStockCount}
          accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} alert />
        <StatCard label="Expired"       value={expiredStockCount}
          accent={accent} textPri={textPri} textSec={textSec} cardBg={cardBg} border={border} alert />
      </Box>

      {/* ── Bar charts ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{
          flex: "1 1 300px",
          backgroundColor: cardBg, border: `1px solid ${border}`,
          borderRadius: "12px", p: "1.25rem 1.5rem",
        }}>
          <SectionHeading
            icon={<BarChartIcon sx={{ fontSize: "18px" }} />}
            title="Best Sellers by Revenue"
            subtitle="Top products by revenue generated"
            accent={accent} textPri={textPri} textSec={textSec}
          />
          <BarChart
            label={itemRevenues}
            data={[{
              label: "Revenue",
              data: itemRevenuesData,
              fill: false,
              backgroundColor: accent,
              tension: 0.1,
            }]}
          />
        </Box>

        <Box sx={{
          flex: "1 1 300px",
          backgroundColor: cardBg, border: `1px solid ${border}`,
          borderRadius: "12px", p: "1.25rem 1.5rem",
        }}>
          <SectionHeading
            icon={<StarIcon sx={{ fontSize: "18px" }} />}
            title="Best Sellers by Units Sold"
            subtitle="Top products by volume"
            accent={accent} textPri={textPri} textSec={textSec}
          />
          <BarChart
            label={itemSales}
            data={[{
              label: "Units Sold",
              data: itemSalesData,
              fill: false,
              backgroundColor: "#5DCAA5",
              tension: 0.1,
            }]}
          />
        </Box>
      </Box>

      {/* ── Tables ── */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
        <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", p: "1.25rem 1.25rem 0.75rem", overflow: "hidden" }}>
          <SectionHeading icon={<InventoryIcon sx={{ fontSize: "18px" }} />}
            title="Top Products" subtitle={`${itemsByRevenue.length} products`}
            accent={accent} textPri={textPri} textSec={textSec} />
          <ReportTable
            columns={[{ label: "Item" }, { label: "Units", align: "center" }, { label: "Revenue", align: "right" }]}
            rows={itemsByRevenue}
            emptyMessage="No product data found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.product_name}</RCell>
                <RCell align="center">{row.item_units}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.item_revenue))}</RCell>
              </RRow>
            )}
          />
        </Box>

        <Box sx={{ flex: "1 1 300px", backgroundColor: cardBg, border: `1px solid ${border}`, borderRadius: "12px", p: "1.25rem 1.25rem 0.75rem", overflow: "hidden" }}>
          <SectionHeading icon={<CategoryIcon sx={{ fontSize: "18px" }} />}
            title="Top Categories" subtitle={`${categoryList.length} categories`}
            accent={accent} textPri={textPri} textSec={textSec} />
          <ReportTable
            columns={[{ label: "Category" }, { label: "Qty", align: "center" }, { label: "Amount", align: "right" }]}
            rows={categoryList}
            emptyMessage="No category data found"
            renderRow={(row, index) => (
              <RRow key={row.id ?? index} index={index}>
                <RCell>{row.category_name}</RCell>
                <RCell align="center">{row.quantity_invoiced}</RCell>
                <RCell align="right">{currencyFormatter(parseFloat(row.invoice_amount))}</RCell>
              </RRow>
            )}
          />
        </Box>
      </Box>
    </Box>
  );
}